<template>
    <div class="es">
        <template v-if="!circuitType">
            <div v-if="sets.length" class="pfeb-set-list">
                <div v-for="(set, i) in sets" :key="i" class="pfeb-set-row" @click="updateSet(set, i)">
                    <div class="pfeb-set-num">{{ i + 1 }}</div>
                    <div class="pfeb-set-field">
                        <span class="pfeb-set-lbl">{{ primaryLabel(set) }}</span>
                        <kimsa-text-field class="es-cell" dense no-elevation centered-value force-value custom-keypress
                            :value="primaryValue(set)" @keypress="onRepsKeypress" @change="onPrimaryChange($event, i)"
                            @click.native.stop />
                    </div>
                    <div v-if="microOf(set)" class="pfeb-set-field">
                        <span class="pfeb-set-lbl">{{ $t('exercises.micropause') }}</span>
                        <kimsa-text-field class="es-cell" dense no-elevation centered-value force-value custom-keypress
                            suffix="s" :value="microOf(set).value" @keypress="onNumberKeypress"
                            @change="onMicroChange($event, i)" @click.native.stop />
                    </div>

                    <div class="es-set-icons" @click.stop>
                        <button-tooltip :tooltip="$t('common.edit')" top>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn icon x-small v-on="on" v-bind="attrs" @click="updateSet(set, i)">
                                    <v-icon size="15">mdi-tune-variant</v-icon>
                                </v-btn>
                            </template>
                        </button-tooltip>
                        <button-tooltip :tooltip="$t('common.duplicate')" top>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn icon x-small v-on="on" v-bind="attrs" @click="duplicate(set, i)">
                                    <v-icon size="15">mdi-content-copy</v-icon>
                                </v-btn>
                            </template>
                        </button-tooltip>
                        <button-tooltip :tooltip="$t('common.delete')" top>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn icon x-small v-on="on" v-bind="attrs" @click="deleteSet(i)">
                                    <v-icon size="15" color="error">mdi-close</v-icon>
                                </v-btn>
                            </template>
                        </button-tooltip>
                    </div>

                    <v-menu offset-y>
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn icon x-small class="es-set-menu" v-on="on" v-bind="attrs" @click.stop>
                                <v-icon size="16">mdi-dots-vertical</v-icon>
                            </v-btn>
                        </template>
                        <v-list dense>
                            <v-list-item link @click="updateSet(set, i)">
                                <v-icon size="18" color="txtSecondary" class="mr-2">mdi-tune-variant</v-icon>
                                <v-list-item-title>{{ $t('common.edit') }}</v-list-item-title>
                            </v-list-item>
                            <v-list-item link @click="duplicate(set, i)">
                                <v-icon size="18" color="txtSecondary" class="mr-2">mdi-content-copy</v-icon>
                                <v-list-item-title>{{ $t('common.duplicate') }}</v-list-item-title>
                            </v-list-item>
                            <v-divider />
                            <v-list-item link @click="deleteSet(i)">
                                <v-icon size="18" color="error" class="mr-2">mdi-trash-can-outline</v-icon>
                                <v-list-item-title class="error--text">{{ $t('common.delete') }}</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </div>
            </div>
            <div v-else class="es-noset">{{ $t('exercises.no_config_exercise_yet') }}</div>

            <div class="pfeb-detail-actions">
                <v-btn text small :color="accentColor" class="es-action" @click="onAddSet">
                    <v-icon left size="16">mdi-plus</v-icon>{{ $tc('exercises.sets', 1) }}
                </v-btn>
                <v-btn text small :color="accentColor" class="es-action" @click="$emit('click:rest')">
                    <v-icon left size="16">mdi-timer-outline</v-icon>
                    {{ hasTime ? $t('exercises.edit_rest') : $t('exercises.pause') }}
                </v-btn>
                <v-btn text small :color="accentColor" class="es-action" @click="showPrescriptionSet">
                    <v-icon left size="16">mdi-clipboard-text-outline</v-icon>{{ $t('common.prescription') }}
                </v-btn>
            </div>

            <div v-if="hasTime" class="es-pause">
                <v-icon size="15">mdi-alarm-snooze</v-icon>
                <span class="es-pause-txt">{{ $t('exercises.rest') }} {{ exercise.time }}</span>
                <button-tooltip :tooltip="$t('common.delete')" top>
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn icon x-small v-on="on" v-bind="attrs" @click="deleteRest">
                            <v-icon size="15" color="error">mdi-close</v-icon>
                        </v-btn>
                    </template>
                </button-tooltip>
            </div>

            <div class="pfeb-detail-chips">
                <div class="pfeb-toggle-chip" :class="{ active: eachSide }" @click="eachSide = !eachSide">
                    <div class="pfeb-chip-dot"></div>{{ $t('exercises.each_side') }}
                </div>
                <div class="pfeb-toggle-chip" :class="{ active: painScale }" @click="painScale = !painScale">
                    <div class="pfeb-chip-dot"></div>{{ $t('common.pain_scale') }}
                </div>
                <div class="es-copy-actions">
                    <button-tooltip top :tooltip="$t('tooltips.copy_prescription')">
                        <template v-slot:activator="{ on, attrs }">
                            <button v-on="on" v-bind="attrs" type="button" class="es-copy-btn"
                                :disabled="!sets.length" @click="copyPrescription">{{ $t('common.copy') }}</button>
                        </template>
                    </button-tooltip>
                    <button-tooltip top :tooltip="$t('tooltips.paste_prescription')">
                        <template v-slot:activator="{ on, attrs }">
                            <button v-on="on" v-bind="attrs" type="button" class="es-copy-btn"
                                :disabled="!prescriptionClipboard" @click="pastePrescription">{{ $t('common.paste') }}</button>
                        </template>
                    </button-tooltip>
                </div>
            </div>

            <kimsa-text-field class="es-comment" dense no-elevation :value="commentary"
                :placeholder="`${$t('common.add')} ${$t('common.comment')}`" @change="commentary = $event" />
        </template>

        <template v-else>
            <v-row v-if="!isTabata" dense align="center">
                <v-col cols="3">
                    <kimsa-text-field dense no-elevation centered-value force-value custom-keypress
                        :placeholder="$t('common.value')" :value="reps" @keypress="onRepsKeypress"
                        @change="reps = $event; emitCircuit()" />
                </v-col>
                <v-col cols="3">
                    <kimsa-combobox dense :placeholder="$t('exercises.types')" :items="repsOptions"
                        @change="onCircuitType" :value="repsType" ref="repsType" />
                </v-col>
                <v-col cols="6">
                    <kimsa-text-field dense no-elevation :value="note" :max-length="6" :placeholder="$t('common.note')"
                        @change="note = $event; emitCircuit()" />
                </v-col>
            </v-row>
            <div v-else>
                <div class="es-noset">{{ $t('circuits.tabata') }}</div>
                <kimsa-text-field dense no-elevation :value="note" :max-length="6" :placeholder="$t('common.note')"
                    @change="note = $event; emitCircuit()" />
            </div>
        </template>

        <set-form-dialog v-if="!circuitType" :value="dialog" :edit-mode="editMode" :is-first-set="isFirstSet"
            :has-weight="hasWeight" :has-micro-pause="hasMicroPause" :disabled-save="!isSetValid"
            :reps-options="repsOptions" :rpe-options="rpeOptions" :tempo-options="tempoOptions"
            :intensity-options="intensityOptions" :reps.sync="reps" :rpe.sync="rpe" :tempo.sync="tempo"
            :weight.sync="weight" :intensity.sync="intensity" :micropause.sync="micropause" :reps-type.sync="repsType"
            :rpe-type.sync="rpeType" :tempo-type.sync="tempoType" :intensity-type.sync="intensityType" @save="saveSet"
            @close="cancelSet" />

        <set-prescription-dialog is-second-dialog @set="onSetPrescription" ref="setPrescription"
            :category="categoryExercise" :coach-id="coachId" />
    </div>
</template>

<script>
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import ButtonTooltip from "@/components/common/ButtonTooltip";
import SetPrescriptionDialog from './SetPrescriptionDialog.vue';
import SetFormDialog from './SetFormDialog.vue';
import {
    SET_NAMES, getRepsOptions, getRpeOptions, getTempoOptions, getIntensityOptions, findSetData, setOptionByShortName,
    validateRepsKey, validateNumberKey, cleanNumberValue
} from "@/utils/Exercises";
import { mapMutations, mapState } from "vuex";

export default {
    name: "ExerciseSeriesEditor",
    components: { KimsaCombobox, KimsaTextField, ButtonTooltip, SetPrescriptionDialog, SetFormDialog },
    props: {
        exercise: {
            type: Object,
            default: null,
        },
        categoryExercise: {
            type: String,
            default: null,
        },
        circuitType: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            sets: [],
            dialog: false,
            editMode: false,
            index: null,
            repsType: null,
            rpeType: null,
            tempoType: null,
            hasWeight: false,
            intensityType: null,
            reps: 1,
            rpe: 0,
            tempo: 0,
            weight: 0,
            intensity: 0,
            micropause: 0,
            note: '',
            eachSide: false,
            painScale: false,
            commentary: '',
            rpeOptions: getRpeOptions(),
            tempoOptions: getTempoOptions(),
            intensityOptions: getIntensityOptions(),
        }
    },
    computed: {
        ...mapState(['prescriptionClipboard']),
        accentColor(vm = this) {
            return vm.$vuetify.theme.dark ? 'primary' : 'accent'
        },
        hasTime(vm = this) {
            return !!(vm.exercise?.hasTime || vm.exercise?.time)
        },
        repsOptions(vm = this) {
            return getRepsOptions(vm.hasIntensity)
        },
        isSetValid(vm = this) {
            if (vm.repsType && !vm.reps) return false
            return true
        },
        isFirstSet(vm = this) {
            if (vm.sets.length === 0) return true
            return vm.index === 0
        },
        isStrongCategory(vm = this) {
            return vm.categoryKey(vm.categoryExercise) === vm.CATEGORY.STRONG || vm.categoryKey(vm.categoryExercise) === vm.CATEGORY.STABILITY || vm.categoryKey(vm.categoryExercise) === vm.CATEGORY.MOBILITY
        },
        hasMicroPause(vm = this) {
            return vm.categoryKey(vm.categoryExercise) !== vm.CATEGORY.STABILITY && vm.categoryKey(vm.categoryExercise) !== vm.CATEGORY.MOBILITY
        },
        hasIntensity(vm = this) {
            return vm.categoryKey(vm.categoryExercise) === vm.CATEGORY.SPEED || vm.categoryKey(vm.categoryExercise) === vm.CATEGORY.RESISTANCE
        },
        coachId(vm = this) {
            return vm.account?.studentSubscription?.coachId || vm.currentUser?.uid || null
        },
        isTabata(vm = this) {
            return vm.circuitType === 'tabata'
        },
    },
    mounted() {
        this.setDefaults()
        if (this.circuitType && !this.isTabata) this.emitCircuit()
    },
    methods: {
        ...mapMutations(['setPrescriptionClipboard']),
        primaryLabel(set) {
            return set?.data?.[0]?.name || this.$t('exercises.reps')
        },
        primaryValue(set) {
            return set?.data?.[0]?.value
        },
        microOf(set) {
            return set?.data?.find(d => d.short_name === 'short_micropause') || null
        },
        onPrimaryChange(value, i) {
            const vm = this
            if (!vm.sets[i]?.data?.[0]) return
            vm.sets[i].data[0].value = value
            vm.emitConfig()
        },
        onMicroChange(value, i) {
            const vm = this
            const micro = vm.microOf(vm.sets[i])
            if (!micro) return
            micro.value = cleanNumberValue(value)
            vm.emitConfig()
        },
        onRepsKeypress(event) {
            validateRepsKey(event)
        },
        onNumberKeypress(event) {
            validateNumberKey(event)
        },
        emitConfig() {
            this.$emit('update', { config: { sets: this.sets } })
        },
        copyPrescription() {
            const vm = this
            if (!vm.sets.length) return
            vm.setPrescriptionClipboard(JSON.parse(JSON.stringify(vm.sets)))
            vm.toast(vm.$t('common.prescription_copied'))
        },
        pastePrescription() {
            const vm = this
            if (!vm.prescriptionClipboard) return
            vm.sets = JSON.parse(JSON.stringify(vm.prescriptionClipboard))
            vm.emitConfig()
            vm.toast(vm.$t('common.prescription_pasted'))
        },
        emitCircuit() {
            const vm = this
            const type = vm.repsType?.value || 'reps'
            vm.$emit('update', { config: { note: vm.note, type, value: vm.reps } })
        },
        onCircuitType(type) {
            this.repsType = type
            this.emitCircuit()
        },
        onAddSet() {
            const vm = this
            if (vm.sets.length > 0) {
                let template = JSON.parse(JSON.stringify(vm.sets[vm.sets.length - 1]))
                vm.sets.push(template)
                return
            }
            vm.createSet()
        },
        createSet() {
            const vm = this
            vm.editMode = false
            vm.index = null
            vm.dialog = true
        },
        cancelSet() {
            const vm = this
            vm.dialog = false
            vm.editMode = false
            vm.index = null
        },
        updateSet(set, index) {
            const vm = this
            vm.editMode = true
            vm.index = index

            let reps = findSetData(set, vm.hasIntensity ? SET_NAMES.REPS_INTENSITY : SET_NAMES.REPS)
            vm.repsType = setOptionByShortName(vm.repsOptions, reps?.short_name)
            vm.reps = reps && reps.value || 1
            let rpe = vm.isStrongCategory ? findSetData(set, SET_NAMES.RPE) : null
            vm.rpeType = setOptionByShortName(vm.rpeOptions, rpe?.short_name)
            vm.rpe = rpe && rpe.value || 0
            let tempo = findSetData(set, SET_NAMES.TEMPO)
            vm.tempoType = setOptionByShortName(vm.tempoOptions, tempo?.short_name)
            vm.tempo = tempo && tempo.value || 0
            let weight = set.data.find(data => data.short_name === 'short_weight')
            vm.hasWeight = !!weight
            vm.weight = weight && weight.value || 0
            let intensity = vm.hasIntensity ? findSetData(set, SET_NAMES.INTENSITY) : null
            vm.intensityType = setOptionByShortName(vm.intensityOptions, intensity?.short_name)
            vm.intensity = intensity && intensity.value || 0
            let micropause = set.data.find(data => data.short_name === 'short_micropause')
            vm.micropause = !!micropause && micropause.value || 0
            vm.dialog = true
        },
        saveSet() {
            const vm = this
            let payload = { data: [] }
            let changeReps = false
            let changeWeight = false
            let changeTempo = false
            let changeIntensity = false

            if (vm.sets.length > 0) {
                if (vm.repsType && vm.sets[0].data[0].name !== vm.repsType.name) changeReps = true
                if (vm.rpeType && vm.sets[0].data[1] && vm.sets[0].data[1].name !== vm.rpeType.name) changeWeight = true
                if (vm.tempoType && vm.sets[0].data[2] && vm.sets[0].data[2].name !== vm.tempoType.name) changeTempo = true
                if (vm.intensityType && vm.sets[0].data[1] && vm.sets[0].data[1].name !== vm.intensityType.name) changeIntensity = true
            }

            if (vm.repsType) payload.data.push({ short_name: `short_${vm.repsType.value}`, value: vm.reps })
            if (vm.rpeType) payload.data.push({ short_name: `short_${vm.rpeType.value}`, value: vm.rpe })
            if (vm.tempoType) payload.data.push({ short_name: `short_${vm.tempoType.value}`, value: vm.tempo })
            if (vm.hasWeight) payload.data.push({ short_name: 'short_weight', value: vm.weight })
            if (vm.intensityType) payload.data.push({ short_name: `short_${vm.intensityType.value}`, value: vm.intensity })
            if (vm.hasMicroPause) payload.data.push({ short_name: 'short_micropause', value: vm.micropause })

            vm.editMode ? vm.sets.splice(vm.index, 1, payload) : vm.sets.push(payload)

            if (vm.sets.length > 0 && vm.index === 0 && (changeReps || changeWeight || changeTempo || changeIntensity)) {
                vm.sets.forEach((set, i) => {
                    if (changeReps) {
                        vm.sets[i].data[0].short_name = `short_${vm.repsType.value}`
                        if (isNaN(vm.sets[i].data[0].value)) vm.sets[i].data[0].value = 1
                    }
                    if (changeWeight) {
                        vm.sets[i].data[1].short_name = `short_${vm.rpeType.value}`
                    }
                    if (changeTempo) {
                        vm.sets[i].data[2].short_name = `short_${vm.tempoType.value}`
                    }
                    if (changeIntensity) {
                        vm.sets[i].data[1].short_name = `short_${vm.intensityType.value}`
                    }
                })
            }
            vm.emitConfig()
            vm.cancelSet()
        },
        duplicate(set, index) {
            const vm = this
            let template = JSON.parse(JSON.stringify(set))
            vm.sets.splice(index + 1, 0, template)
        },
        deleteSet(index) {
            this.sets.splice(index, 1)
        },
        setDefaults() {
            const vm = this
            if (vm.exercise) {
                vm.eachSide = vm.exercise.eachSide || false
                vm.painScale = vm.exercise.painScale || false
                vm.commentary = vm.exercise.commentary || ''
                vm.note = vm.exercise?.config?.note || ''
            }
            const config = vm.exercise?.config || null
            if (vm.circuitType) {
                const type = config?.type || 'reps'
                vm.repsType = vm.repsOptions.find(o => o.value === type) || vm.repsOptions[0]
                vm.reps = config?.value || 1
                return
            }
            if (config && config.sets && config.sets.length > 0) {
                vm.sets = JSON.parse(JSON.stringify(config.sets))
                const firstSet = vm.sets[0]
                let repsType = findSetData(firstSet, vm.hasIntensity ? SET_NAMES.REPS_INTENSITY : SET_NAMES.REPS)
                vm.repsType = setOptionByShortName(vm.repsOptions, repsType?.short_name)
                let rpeType = vm.isStrongCategory ? findSetData(firstSet, SET_NAMES.RPE) : null
                vm.rpeType = setOptionByShortName(vm.rpeOptions, rpeType?.short_name)
                let tempoType = findSetData(firstSet, SET_NAMES.TEMPO)
                vm.tempoType = setOptionByShortName(vm.tempoOptions, tempoType?.short_name)
                if ((firstSet && firstSet.data.find(data => data.short_name === 'short_weight')) || vm.isStrongCategory) vm.hasWeight = true
                let intensityType = vm.hasIntensity ? findSetData(firstSet, SET_NAMES.INTENSITY) : null
                vm.intensityType = setOptionByShortName(vm.intensityOptions, intensityType?.short_name)
            } else {
                vm.repsType = vm.repsOptions[0]
                if (vm.isStrongCategory) {
                    vm.rpeType = vm.rpeOptions[0]
                    vm.tempoType = vm.tempoOptions[0]
                    vm.hasWeight = true
                }
                if (vm.hasIntensity) vm.intensityType = vm.intensityOptions[0]
            }
        },
        showPrescriptionSet() {
            this.$refs.setPrescription.show()
        },
        onSetPrescription(prescription) {
            const vm = this
            vm.eachSide = prescription.eachSide || false
            vm.painScale = prescription.painScale || false
            vm.commentary = prescription.commentary || ''
            vm.$nextTick(() => {
                vm.sets = prescription.config.sets
                vm.emitConfig()
                vm.$emit('update', { time: prescription.time || '', hasTime: !!prescription.time })
            })
        },
        deleteRest() {
            this.$emit('update', { hasTime: false, time: '' })
        },
    },
    watch: {
        eachSide(val) {
            this.$emit('update', { eachSide: val })
        },
        painScale(val) {
            this.$emit('update', { painScale: val })
        },
        commentary(val) {
            this.$emit('update', { commentary: val })
        },
    }
}
</script>

<style scoped>
.es {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.es-copy-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-left: auto;
}

.es-copy-btn {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .06em;
    color: var(--v-accent-base);
    background: transparent;
    border: none;
    cursor: pointer;
}

.es-copy-btn:disabled {
    color: var(--v-txtMuted-base);
    cursor: default;
}

.pfeb-set-list {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.pfeb-set-row {
    display: flex;
    align-items: center;
    gap: 8px;
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 8px;
    padding: 6px 10px;
    cursor: pointer;
    transition: background 0.12s, border-color 0.12s;
}

.pfeb-set-row:hover {
    background: var(--v-surface2-base);
    border-color: var(--v-outline2-base);
}

.pfeb-set-num {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    flex-shrink: 0;
    font-size: 10px;
    font-weight: 800;
    display: flex;
    align-items: center;
    justify-content: center;
}

.theme--dark .pfeb-set-num {
    background: var(--v-primary-base);
    color: #0C0E12;
}

.theme--light .pfeb-set-num {
    background: var(--v-accent-base);
    color: #fff;
}

.pfeb-set-field {
    display: flex;
    align-items: center;
    gap: 6px;
    flex: 1;
    min-width: 0;
}

.pfeb-set-lbl {
    font-size: 10px;
    font-weight: 700;
    color: var(--v-txtMuted-base);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    white-space: nowrap;
}

.es-cell {
    width: 74px;
    flex-shrink: 0;
}

.es-set-icons {
    display: flex;
    align-items: center;
    flex-shrink: 0;
}

.es-set-menu {
    display: none !important;
}

.es-noset {
    font-size: 12px;
    color: var(--v-txtMuted-base);
    padding: 6px 0;
}

.pfeb-detail-actions {
    display: flex;
    gap: 6px;
}

.es-action {
    flex: 1;
    border: 1px solid var(--v-outline-base) !important;
}

.es-pause {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 10px;
    border-radius: 8px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
}

.es-pause .v-icon {
    color: var(--v-txtSecondary-base) !important;
}

.es-pause-txt {
    flex: 1;
    font-size: 12px;
    color: var(--v-txtSecondary-base);
}

.pfeb-detail-chips {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.pfeb-toggle-chip {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 20px;
    border: 1px solid var(--v-outline-base);
    font-size: 12px;
    font-weight: 600;
    color: var(--v-txtSecondary-base);
    cursor: pointer;
    transition: all 0.15s;
}

.theme--dark .pfeb-toggle-chip.active {
    background: rgba(204, 255, 51, 0.12);
    border-color: var(--v-primary-base);
    color: var(--v-primary-base);
}

.theme--light .pfeb-toggle-chip.active {
    background: rgba(51, 153, 153, 0.1);
    border-color: var(--v-accent-base);
    color: var(--v-accent-base);
}

.pfeb-chip-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--v-outline-base);
    transition: background 0.15s;
}

.theme--dark .pfeb-toggle-chip.active .pfeb-chip-dot {
    background: var(--v-primary-base);
}

.theme--light .pfeb-toggle-chip.active .pfeb-chip-dot {
    background: var(--v-accent-base);
}

@media (max-width: 600px) {
    .es-set-icons {
        display: none;
    }

    .es-set-menu {
        display: inline-flex !important;
    }

    .pfeb-set-row {
        gap: 8px;
        padding: 6px 8px;
    }

    .es-cell {
        width: 54px;
    }

    .es-set-menu {
        margin-left: 4px;
    }

    .es-action {
        min-width: 0 !important;
        padding: 0 8px !important;
    }
}
</style>
